import { ButtonType } from '@mm/state/model/ButtonModel';
import React, { useState } from 'react';
import { InputGroup } from 'reactstrap';
import { Field } from 'redux-form';
import AttributePicker from '../../../../../../common/components/AttributePicker/AttributePicker';
import {
  generatePageStructure,
  nameById,
} from '@mm/state/helpers/filterable-select-helpers';
import Select from '../../../../../../common/components/FilterableSelect/FilterableSelect';
import { ReduxFormPhoneInput } from '../../../../../../common/components/PhoneInput/PhoneInput';
import TextInput from '../../../../../../common/components/TextInput';
import AttributeContainingTextInput from '../../../../../components/AttributeContainingTextInput/AttributeContainingTextInput';
import { makeButton } from '../../../requestModels/widgets/buttonStackWidget/Button';
import './constants.scss';

const pageButton = (
  <span>
    <i className="far fa-file-alt pr-2" aria-hidden="true" />
    Dialogue
  </span>
);

const callButton = (
  <span>
    <i className="fa fa-phone pr-2" aria-hidden="true" />
    Phone
  </span>
);

const urlButton = (
  <span>
    <i className="fas fa-external-link-alt pr-2" aria-hidden="true" />
    URL
  </span>
);

const shareButton = (
  <span>
    <i className="fas fa-share-alt pr-2" aria-hidden="true" />
    Share
  </span>
);

const handoverButton = (
  <span>
    <i className="fas fa-hands-helping pr-2" aria-hidden="true" />
    Handover
    <span className="button-config__icon_pro_badge">BASIC</span>
  </span>
);

export const ReduxFormInput = ({
  input: { onChange, onBlur, onFocus, value },
  isShare,
  ...props
}) => {
  const modifiedValue = isShare ? 'Share' : value;
  return (
    <TextInput
      {...props}
      value={modifiedValue}
      onChange={onChange}
      onFocus={onFocus}
      onBlur={onBlur}
    />
  );
};
export const ReduxFormInputWithAttribute = ({
  input: { onChange, onBlur, onFocus, value },
  ...props
}) => {
  const [textInputRef, setTextInputRef] = useState(null);
  const onAttributePicked = attribute => {
    onChange(`${value}{{${attribute}}}`);
    setTimeout(() => {
      if (textInputRef) {
        textInputRef.focus();
      }
    }, 100);
  };
  return (
    <div className="button-config__input-url-field">
      <AttributeContainingTextInput
        {...props}
        className="button-config__input-url-field"
        inputRef={setTextInputRef}
        value={value}
        onChange={e => onChange(e.target.value)}
        onFocus={onFocus}
        onBlur={onBlur}
        useStoreAttributes
      />
      <AttributePicker
        className="button-config__input-url-field-attribute-picker"
        onAttributePicked={onAttributePicked}
      />
    </div>
  );
};

export const ReduxSelectInput = ({
  input: { onChange, value },
  children,
  ...props
}) => (
  <Select
    {...props}
    onChange={onChange}
    outerValue={nameById(value, props.pages)}
  >
    {children}
  </Select>
);

export const PageField = ({ pages, pageGroups }) => (
  <Field
    name="page"
    type="select"
    component={ReduxSelectInput}
    pages={pages}
    suggestions={generatePageStructure(pages, pageGroups)}
  >
    {pages.map(page => (
      <option key={page.id} value={page.id}>
        {page.name}
      </option>
    ))}
  </Field>
);

export const CallField = () => (
  <InputGroup className="button-config__input">
    <Field
      name="phone"
      type="tel"
      component={ReduxFormPhoneInput}
      placeholder="Enter phone number"
    />
  </InputGroup>
);

export const URLField = () => (
  <Field
    name="url"
    type="url"
    component={ReduxFormInputWithAttribute}
    placeholder="Enter url"
  />
);

export const HandoverField = () => {
  const onlyNumbers = value => value.replace(/[^\d]/g, '');
  return (
    <>
      <div className="button-config__text">
        Developer Feature: Pass this conversation to another Messenger
        application using&nbsp;
        <a
          href="https://developers.facebook.com/docs/messenger-platform/handover-protocol/"
          target="_blank"
          rel="noopener noreferrer"
        >
          Facebook&#39;s Handover Protocol
        </a>
        .
      </div>
      <InputGroup className="button-config__input">
        <Field
          name="appId"
          type="appId"
          component={ReduxFormInput}
          placeholder="Enter App ID"
          normalize={onlyNumbers}
        />
      </InputGroup>
    </>
  );
};

// TODO: Uncomment and modify as needed once this part lands as a separate follow-up task
// export const ShareField = () => (
//   <InputGroup className="button-config__input">
//     <Field
//      name="share"
//      type="share"
//      component={ReduxFormInput}
//      placeholder="Enter button name"
//    />
//   </InputGroup>
// );

export const MAP_FIELD = {
  [ButtonType.PAGE]: PageField,
  [ButtonType.CALL]: CallField,
  [ButtonType.URL]: URLField,
  [ButtonType.HANDOVER]: HandoverField,
  // [ButtonType.SHARE]: ShareField,
};

export const MAP_BUTTON = {
  [ButtonType.PAGE]: pageButton,
  [ButtonType.CALL]: callButton,
  [ButtonType.URL]: urlButton,
  [ButtonType.HANDOVER]: handoverButton,
  [ButtonType.SHARE]: shareButton,
};

export const MAP_ENTITY = {
  [ButtonType.PAGE]: ({ formValues, ...props }) =>
    makeButton({
      type: ButtonType.PAGE,
      name: formValues.name,
      sortOrder: props.buttonIndex,
      id: props.button.id,
      tags: formValues.tags,
      configExtendObject: {
        pageId: formValues.page,
      },
    }),
  [ButtonType.CALL]: ({ formValues, ...props }) =>
    makeButton({
      type: ButtonType.CALL,
      name: formValues.name,
      sortOrder: props.buttonIndex,
      id: props.button.id,
      tags: formValues.tags,
      configExtendObject: {
        phoneNumber: formValues.phone,
      },
    }),
  [ButtonType.URL]: ({ formValues, ...props }) =>
    makeButton({
      type: ButtonType.URL,
      name: formValues.name,
      sortOrder: props.buttonIndex,
      id: props.button.id,
      tags: formValues.tags,
      configExtendObject: {
        webviewHeightRatio: formValues.webviewSize,
        url: formValues.url,
      },
    }),
  [ButtonType.HANDOVER]: ({ formValues, ...props }) =>
    makeButton({
      type: ButtonType.HANDOVER,
      name: formValues.name,
      sortOrder: props.buttonIndex,
      id: props.button.id,
      tags: formValues.tags,
      configExtendObject: {
        appId: formValues.appId,
      },
    }),
  [ButtonType.SHARE]: ({ formValues, ...props }) =>
    makeButton({
      type: ButtonType.SHARE,
      name: formValues.name,
      sortOrder: props.buttonIndex,
      id: props.button.id,
      tags: formValues.tags,
    }),
};

export const PRO_BUTTON_TYPES = [ButtonType.HANDOVER];

export const ButtonConfigFormTypes = [
  ButtonType.PAGE,
  ButtonType.CALL,
  ButtonType.URL,
  ButtonType.HANDOVER,
];

export const MenuButtonConfigFormTypes = [ButtonType.PAGE, ButtonType.URL];
